import { MutableRefObject } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { IntlShape } from 'react-intl/src/types';
import { getUserLabelRequiredByOrganization } from 'shared/domain/user/mapping/toView';
import { DocumentState, IssueState } from '../types';
import { LevelEntity } from 'shared/domain/level/types/entity';
import { SiteEntity } from 'shared/domain/site/types/entity';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { GridApiCommon } from '@mui/x-data-grid-pro';
import {
  calculateInspectionScoreFromProtocol,
  formatInspectionScorePercent,
  type InspectionScoreProtocolItem,
} from 'shared/domain/inspection/inspectionScore';
import { toJSDate } from 'shared/utils/date';
import { LabelledEntity } from 'shared/types/commonView';
import { NOT_APPLICABLE_FIELD_INDICATOR } from 'shared/contants';
import {
  DocumentationViewItemType,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import { getCountryName } from 'shared/domain/country/countries';
import type { CompanyAddressKind } from 'shared/domain/company/types/model';
import { findVersionById } from 'shared/domain/documentation/currentVersion';

export interface DocumentationValueGetters {
  modifiedAt: (field: string, row: DocumentationModel) => Date | null;
  createdAt: (field: string, row: DocumentationModel) => Date | null;
  createdBy: (field: string, row: DocumentationModel) => LabelledEntity[];
  modifiedBy: (field: string, row: DocumentationModel) => LabelledEntity[];
  sites: (field: string, row: DocumentationModel) => SiteEntity[];
  levels: (field: string, row: DocumentationModel) => LevelEntity[];
  state: (field: string, row: DocumentationModel) => DocumentState | null;
  currentVersion: (field: string, row: DocumentationModel) => number | null;
}

interface CreateDocumentationValueGettersArgs {
  organizationId: string;
  intl: IntlShape;
}

function mapUserToLabelledEntity(
  user: DocumentationModel['createdBy'] | DocumentationModel['modifiedBy'],
  organizationId: string,
  intl: IntlShape
): LabelledEntity | null {
  if (!user?._id) {
    return null;
  }

  const label =
    getUserLabelRequiredByOrganization(user, organizationId, intl) ?? '';

  return {
    _id: user._id,
    label,
  };
}

export function createDocumentationValueGetters({
  organizationId,
  intl,
}: CreateDocumentationValueGettersArgs): DocumentationValueGetters {
  return {
    modifiedAt: modifiedAtValueGetter,
    createdAt: createdAtValueGetter,
    createdBy: (_, row: DocumentationModel) => {
      const createdBy = mapUserToLabelledEntity(
        row.createdBy,
        organizationId,
        intl
      );
      return createdBy ? [createdBy] : [];
    },
    modifiedBy: (_, row: DocumentationModel) => {
      const modifiedBy = mapUserToLabelledEntity(
        row.modifiedBy,
        organizationId,
        intl
      );
      return modifiedBy ? [modifiedBy] : [];
    },
    sites: sitesValueGetter,
    levels: levelsValueGetter,
    state: documentStateValueGetter,
    currentVersion: documentationCurrentVersionValueGetter,
  };
}

const createSimpleValueGetter = <T, K extends keyof T>(
  key: K,
  fallback: any = null
) => {
  return (_: unknown, row: T): T[K] | typeof fallback => {
    return row?.[key] ?? fallback;
  };
};

const createDateValueGetter = <T extends Record<string, any>>(
  key: keyof T
) => {
  return (_: unknown, row: T): Date | null => {
    return toJSDate(row?.[key]);
  };
};

const createArrayValueGetter = <T, K extends keyof T>(key: K) => {
  return (_: unknown, row: T): T[K] extends Array<any> ? T[K] : [] => {
    return (row?.[key] || []) as any;
  };
};

export const authorCreatedByLabelValueGetter = (
  row: DocumentationModel,
  organizationId: string,
  intl: IntlShape
): string | null => {
  return (
    getUserLabelRequiredByOrganization(
      row?.createdBy,
      organizationId,
      intl
    ) ?? null
  );
};

export const authorModifiedByLabelValueGetter = (
  row: DocumentationModel,
  organizationId: string,
  intl: IntlShape
): string | null => {
  return (
    getUserLabelRequiredByOrganization(
      row?.modifiedBy,
      organizationId,
      intl
    ) ?? null
  );
};

export const labelledCreatedByLabelValueGetter = (
  _: unknown,
  row: { createdBy?: { label?: string } }
): string | null => {
  return row?.createdBy?.label ?? null;
};

export const labelledModifiedByLabelValueGetter = (
  _: unknown,
  row: { modifiedBy?: { label?: string } }
): string | null => {
  return row?.modifiedBy?.label ?? null;
};

type IssueUserField = 'createdBy' | 'modifiedBy';

export const makeIssueUserLabelValueGetter = (
  field: IssueUserField,
  intl: IntlShape
) => (
  _: unknown,
  row: { createdBy?: { label?: string }; modifiedBy?: { label?: string } }
): string => {
  const label = row?.[field]?.label;
  return label ?? formatTranslation(intl, CommonTranslationKeys.userDeleted);
};

export const numberValueGetter = createSimpleValueGetter<{ number: number }, 'number'>('number');

export const createdAtValueGetter = createDateValueGetter<{ createdAt: string }>('createdAt');

export const modifiedAtValueGetter = createDateValueGetter<{ modifiedAt: string }>('modifiedAt');

export const documentStateValueGetter = (
  _: unknown,
  row: { deleted?: boolean; type?: string }
): DocumentState | null => {
  return row?.deleted ? DocumentState.archived : DocumentState.published;
};

export const issueStateValueGetter = (
  _: unknown,
  row: { deleted?: boolean }
): IssueState | null => {
  return row?.deleted ? IssueState.archived : IssueState.published;
};

export const sitesValueGetter = createArrayValueGetter<{ sites?: SiteEntity[] }, 'sites'>('sites');

export const companySitesValueGetter = createArrayValueGetter<{ sites?: Array<{ _id: string; label: string }> }, 'sites'>('sites');

export const levelsValueGetter = createArrayValueGetter<{ levels?: LevelEntity[] }, 'levels'>('levels');

export const orderNumberValueGetter = <T extends { _id: string }>(
  _value: unknown,
  row: T,
  _column: unknown,
  apiRef: MutableRefObject<GridApiCommon>
): number => {
  const allRows = apiRef.current.getSortedRowIds();
  return allRows.indexOf(row._id) + 1;
};

export const processCodeValueGetter = createSimpleValueGetter<{ code: string }, 'code'>('code', '');

export const processValueGetter = (
  _: unknown,
  row: { process: string; renderableProcess: { color: string; code: string } }
): Array<{ _id: string; label: string }> => {
  if (!row?.process) {
    return [];
  }
  return [
    {
      _id: row.process,
      label: row.renderableProcess?.code || '',
    },
  ];
};

export const inspectionNumberValueGetter = createSimpleValueGetter<{ code: string }, 'code'>('code', '');

export const templateSummaryValueGetter = (
  _: unknown,
  row: { template: { summary: string } }
): string => {
  return row?.template?.summary ?? '';
};

export const inspectionStatusValueGetter = (
  _: unknown,
  row: { status: any }
): Array<{ _id: string; label: string }> => {
  if (!row?.status) {
    return [];
  }
  return [
    {
      _id: row.status,
      label: row.status,
    },
  ];
};

export const executionDateValueGetter = (
  _: unknown,
  row: { inspectionDate: any }
): Date | null => {
  return toJSDate(row?.inspectionDate);
};

export const inspectionAuthorValueGetter = (
  _: unknown,
  row: { author: string | null | undefined },
  intl: IntlShape
): string => {
  if (!row?.author) {
    return formatTranslation(intl, CommonTranslationKeys.userDeleted);
  }
  return row.author;
};

export const controlledPartyValueGetter = createArrayValueGetter<{ controlledParty: any[] }, 'controlledParty'>('controlledParty');

export const workTypesValueGetter = createArrayValueGetter<{ workTypes: any[] }, 'workTypes'>('workTypes');

export const documentationRelatedIssuesCountValueGetter = (
  _: unknown,
  row: {
    type?: DocumentationViewItemType;
    relatedIssuesCount?: number;
    levels?: readonly unknown[];
  }
): number | string | null => {
  if (isDirectoryItemType(row?.type)) {
    return null;
  }

  if (!row?.levels?.length) {
    return NOT_APPLICABLE_FIELD_INDICATOR;
  }

  return row?.relatedIssuesCount ?? 0;
};

export const documentationExportedFromIssueValueGetter = (
  _: unknown,
  row: {
    type?: DocumentationViewItemType;
    sourceIssue?: { issueNumber?: string };
  }
): string | null => {
  if (isDirectoryItemType(row?.type)) {
    return null;
  }

  return row?.sourceIssue?.issueNumber ?? null;
};

/** The documentation's current version number (PT-7117), `null` for folders and without a current version. */
export const documentationCurrentVersionValueGetter = (
  _: unknown,
  row: {
    type?: DocumentationViewItemType;
    versions?: DocumentationModel['versions'];
    currentVersionId?: DocumentationModel['currentVersionId'];
  }
): number | null => {
  if (isDirectoryItemType(row?.type) || !row?.versions) {
    return null;
  }

  return findVersionById({ versions: row.versions }, row.currentVersionId)?.number ?? null;
};

export const inspectionRelatedIssuesCountValueGetter = (
  _: unknown,
  row: { relatedIssuesCount?: number }
): number => {
  return row?.relatedIssuesCount ?? 0;
};

export const userLabelValueGetter = createSimpleValueGetter<{ label: string }, 'label'>('label', '');

export const userEmailValueGetter = createSimpleValueGetter<{ email: string }, 'email'>('email', '');

export const userRoleValueGetter = (
  _: unknown,
  row: { role: { _id: string; label: string } }
): string => {
  return row?.role?._id ?? '';
};

export const userPhoneValueGetter = createSimpleValueGetter<{ phone: string }, 'phone'>('phone', '');

export const userSitesValueGetter = createArrayValueGetter<{ sites: Array<{ _id: string; label: string }> }, 'sites'>('sites');

export const userProcessesValueGetter = createArrayValueGetter<{ processes: Array<{ _id: string; label: string }> }, 'processes'>('processes');

export const companyShortLabelValueGetter = createSimpleValueGetter<{ shortLabel: string }, 'shortLabel'>('shortLabel', '');

export const companyLongLabelValueGetter = createSimpleValueGetter<{ longLabel: string }, 'longLabel'>('longLabel', '');

export const finalCostSettledValueGetter = (
  _: unknown,
  row: { financialData?: { finalCost?: { settled?: number } } }
): number | null => {
  return row?.financialData?.finalCost?.settled ?? null;
};

export const finalCostOutstandingValueGetter = (
  _: unknown,
  row: { financialData?: { finalCost?: { outstanding?: number } } }
): number | null => {
  return row?.financialData?.finalCost?.outstanding ?? null;
};

export const estimatedCostValueGetter = (
  _: unknown,
  row: { financialData?: { estimatedCost?: { cost?: number } } }
): number | null => {
  return row?.financialData?.estimatedCost?.cost ?? null;
};

export const expectedFineValueGetter = (
  _: unknown,
  row: { financialData?: { expectedFine?: number } }
): number | null => {
  return row?.financialData?.expectedFine ?? null;
};

export const imposedFineValueGetter = (
  _: unknown,
  row: { financialData?: { imposedFine?: number } }
): number | null => {
  return row?.financialData?.imposedFine ?? null;
};

export const projectInvolvementTypeValueGetter = (
  _: unknown,
  row: { projectInvolvementType: { _id: string; label: string } | string }
): string => {
  if (typeof row?.projectInvolvementType === 'string') {
    return row.projectInvolvementType;
  }
  return row?.projectInvolvementType?._id ?? '';
};

export const taxNumberValueGetter = createSimpleValueGetter<{ taxNumber?: string }, 'taxNumber'>('taxNumber');

export const correspondenceAddressStreetAndNumberValueGetter = (
  _: unknown,
  row: { correspondenceAddress?: { streetAndNumber?: string } }
): string | null => {
  return row?.correspondenceAddress?.streetAndNumber ?? null;
};

export const correspondenceAddressPostalCodeValueGetter = (
  _: unknown,
  row: { correspondenceAddress?: { postalCode?: string } }
): string | null => {
  return row?.correspondenceAddress?.postalCode ?? null;
};

/** The address country (an ISO alpha-3 code) by name in `locale`. */
export function createAddressCountryValueGetter(addressKind: CompanyAddressKind, locale: string) {
  return (
    _: unknown,
    row: Partial<Record<CompanyAddressKind, { country?: string }>>
  ): string | null => {
    const country = row?.[addressKind]?.country;
    return country ? getCountryName(country, locale) : null;
  };
}

export const registeredAddressStreetAndNumberValueGetter = (
  _: unknown,
  row: { registeredOfficeAddress?: { streetAndNumber?: string } }
): string | null => {
  return row?.registeredOfficeAddress?.streetAndNumber ?? null;
};

export const registeredAddressPostalCodeValueGetter = (
  _: unknown,
  row: { registeredOfficeAddress?: { postalCode?: string } }
): string | null => {
  return row?.registeredOfficeAddress?.postalCode ?? null;
};

export const contractLabelValueGetter = createSimpleValueGetter<{ label: string }, 'label'>('label', '');

export const contractPartiesValueGetter = createArrayValueGetter<{ parties: Array<{ _id: string; label: string }> }, 'parties'>('parties');

export const inspectionResultValueGetter = (
  _: unknown,
  row: { protocol?: InspectionScoreProtocolItem[] }
): string => {
  if (!row?.protocol || !Array.isArray(row.protocol)) {
    return 'N/A';
  }

  return formatInspectionScorePercent(calculateInspectionScoreFromProtocol(row.protocol)) ?? 'N/A';
};
